import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import useFetch from '../../../api/useFetch';
import SkeletonCard from '../../../components/cards/SkeletonCard';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LetterStatus } from '../../../components/special/Status';
import Loader from '../../../components/special/Loader';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import NoDataAnimation from '../../../components/animation/NoDataAnimation';
import DateLabel from '../../../components/label/DateLabel';
import ExoTableRecipient from '../../../components/exo/ExoTableRecipient';
import ExoDialog from '../../../components/exo/ExoDialog';
import ExtendLetter from '../../letter/ExtendLetter';
import { NewLetter } from '../../../components/new/NewLetter';
import { useNavigate } from 'react-router-dom';

const LettersTab = ({ project }) => {
  const { apiData, setApiData, isLoading, reloadApiData } = useFetch(
    `letters?projectId[eq]=${project.id}`
  );
  const [edit, setEdit] = useState(null);

  return (
    <Box className="w-full flex flex-col relative">
      <Loader active={isLoading} />
      {!apiData ? (
        <SkeletonCard height="55" />
      ) : (
        <>
          {apiData.data.length === 0 && (
            <Box className="w-full flex justify-center">
              <NoDataAnimation />
            </Box>
          )}
          {apiData.data.map((data) => (
            <Letter
              key={data.id}
              data={data}
              onDelete={reloadApiData}
              onEdit={setEdit}
            />
          ))}
        </>
      )}
      <ExoDialog open={Boolean(edit)} onClose={() => setEdit(null)}>
        <NewLetter
          letters={apiData}
          setLetters={setApiData}
          startData={edit}
          onClose={() => setEdit(null)}
        />
      </ExoDialog>
    </Box>
  );
};

const Letter = ({ data, onDelete, onEdit }) => {
  const navigate = useNavigate();

  return (
    <Accordion
      TransitionProps={{ unmountOnExit: true }}
      sx={{
        '& .letter-link-btn': { opacity: 0, transition: 'opacity 0.2s' },
        '&:hover .letter-link-btn': { opacity: 1 },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box className="flex justify-between items-center w-full gap-2 pr-4">
          <Box className="flex items-center gap-2 min-w-0">
            <Typography
              component="span"
              className="monospace"
              sx={{ fontWeight: 'bold', opacity: 0.6, whiteSpace: 'nowrap' }}
            >
              {data.id}
            </Typography>
            <Typography component="span" noWrap>
              {data.subject}
            </Typography>
            {(data.date || data.createdAt) && (
              <Typography
                component="span"
                variant="caption"
                sx={{ opacity: 0.6, whiteSpace: 'nowrap' }}
              >
                <DateLabel date={data.date || data.createdAt} />
              </Typography>
            )}
            {Boolean(data.attachmentCount) && (
              <Typography
                component="span"
                variant="caption"
                className="flex items-center"
                sx={{ opacity: 0.6, whiteSpace: 'nowrap' }}
              >
                <AttachFileIcon sx={{ fontSize: '0.9rem' }} />
                {data.attachmentCount}
              </Typography>
            )}
            <IconButton
              className="letter-link-btn"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/letters/${data.id}`);
              }}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box className="flex items-center gap-4">
            <Box className="hidden md:block">
              <ExoTableRecipient row={data} />
            </Box>
            <Box sx={{ maxWidth: '200px' }}>
              <LetterStatus number={data.status} />
            </Box>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <ExtendLetter
          row={data}
          onUpdate={() => {}}
          onDelete={onDelete}
          onEdit={onEdit}
        />
      </AccordionDetails>
    </Accordion>
  );
};

export default LettersTab;
